import React, { useEffect, useState } from "react";
import { ClockCircleOutlined, LeftOutlined } from "@ant-design/icons";
import { Col, Row, Select, Spin, Typography, Upload } from "antd";
import styles from "./newsletter-form.module.css";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../../../../common/custom-button/custom-button";
import { Controller, useForm } from "react-hook-form";
import CustomUpload from "../../../../common/CustomUpload/CustomUpload";
import CustomSelect from "../../../../common/input/custom-select/custom-select";
import RichTextEditor from "../../../../components/rich-text-editor/rich-text-editor";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import { AUDIENCE_LIST, COMMUNICATION_CHANNEL, PUBLISHING_SCHEDULE } from "../../../../constants/common";
import CustomDatepicker from "../../../../common/input/custom-datepicker/custom-datepicker";
import CustomTimePicker from "../../../../common/input/custom-timepicker/custom-timepicker";
import dayjs from "dayjs";
import { createNewsletter, getNewsletterById, updateNewsletter } from "../../../../services/communication";
import { toast } from "react-toastify";
import { getAllLevels } from "../../../../services/announcement";
import { createAntdSelection } from "../../../../utils/common";

const NewsLetterForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const newsletterId = params.get("uid");
  const [newsletterDetails, setNewsletterDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [allLevels, setAllLevels] = useState([]);
  const { control, trigger, getValues, watch, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      cover_image: null,
      news_letter: null,
      title: "",
      audience: [],
      levels: [],
      communication_channel: [],
      schedule_published: "",
      schedule_date: "",
      schedule_time: "",
      content: "",
    },
  });
  const schedulePublished = watch("schedule_published");

  useEffect(() => {
    if (newsletterId) {
      getNewsletterData();
    } else {
      setNewsletterDetails(null);
    }
  }, [newsletterId]);

  useEffect(() => {
    getAllLevelsData();
  }, []);

  const getAllLevelsData = async () => {
    try {
      const res = await getAllLevels();
      const data = res?.data?.data?.levels;
      setAllLevels([
        { label: "Select All", value: "selectAll" },
        ...createAntdSelection(data),
      ]);
    } catch (err) {
      console.log(err);
    }
  };

  const getNewsletterData = async () => {
    try {
      setLoading(true);
      const response = await getNewsletterById(newsletterId);
      if (response?.data) {
        const data = response?.data?.data;
        setNewsletterDetails(data);
        reset({
          cover_image: getImageName(data?.cover_image),
          news_letter: getImageName(data?.news_letter),
          title: data?.title ?? "",
          audience: data?.audience ? data?.audience?.split(",") : [],
          levels: data?.levels ? data?.levels : [],
          communication_channel: data?.communication_channel
            ? data?.communication_channel?.split(",")
            : [],
          schedule_published: data?.is_schedule_published ? "later" : "now",
          schedule_date: data?.is_schedule_published ? dayjs(data?.schedule_date) : "",
          schedule_time: data?.is_schedule_published ? dayjs(data?.schedule_time, "HH:mm") : "",
          content: data?.content ?? "",
        });
      }
      setLoading(false);
    } catch (error) {
      console.log("error", error);
      setLoading(false);
    }
  };

  const getImageName = (img) => {
    const imageName = img
      ? decodeURIComponent(img)?.split("#")?.slice(-1)?.[0] ?? null
      : null;
    return imageName ? { name: imageName } : {};
  };

  const handleSubmitForm = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) return;

      const formValues = getValues();
      const formData = new FormData();

      formData.append("title", formValues.title);
      formData.append("content", formValues.content);
      formData.append("audience", formValues.audience.join(","));
      formData.append("levels", JSON.stringify(formValues.levels));
      formData.append("communication_channel", formValues.communication_channel.join(","));

      if (formValues.cover_image?.size) {
        formData.append("cover_image", formValues.cover_image);
      }

      if (formValues.news_letter?.size) {
        formData.append("news_letter", formValues.news_letter);
      }
      
      if (schedulePublished === "later") {
        formData.append("is_schedule_published", "true");
        formData.append(
          "schedule_date",
          dayjs(formValues.schedule_date).format("MM-DD-YYYY")
        );
        formData.append(
          "schedule_time",
          dayjs(formValues.schedule_time).format("HH:mm")
        );
      } else {
        formData.append("is_schedule_published", "false");
      }

      setLoading(true);
      if (newsletterDetails?.id) {
        await updateNewsletter(newsletterDetails?.id, formData);
      } else {
        await createNewsletter(formData);
      }
      toast.success(
        `Newsletter is ${newsletterDetails ? "updated" : "created"} successfully!`
      );
      navigate(-1);
    } catch (err) {
      console.log(err);
      toast.error("Failed to create newsletter");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (selectedValues, allOptions, onChange) => {
    if (selectedValues?.includes("selectAll")) {
      const allWithoutSelectAll = allOptions
        .filter((item) => item.value !== "selectAll")
        .map((item) => item.value);

      onChange(allWithoutSelectAll);
    } else {
      onChange(selectedValues);
    }
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.leftSection} onClick={() => navigate(-1)}>
        <LeftOutlined />
        <Typography className={styles.navTitle}>
          {newsletterId ? "Edit Newsletter" : "Create Newsletter"}
        </Typography>
      </div>
      <div className={styles.newsletterForm}>
        <Row gutter={[32, 20]}>
          <Col span={12}>
            <Controller
              name="cover_image"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomUpload
                  accepts=".jpg,.png,.jpeg"
                  error={error}
                  fileName={value?.name}
                  handleBeforeUpload={(file) => {
                    const accepts = ".jpg,.png,.jpeg";
                    const allowedExtensions = accepts.split(',');
                    const isValid = allowedExtensions.some((ext) =>
                      file?.name?.toLowerCase()?.endsWith(ext)
                    );
                    if (!isValid) {
                      toast.error("Only Images are allowed!");
                      return Upload.LIST_IGNORE;
                    }
                    onChange(file);
                    return false;
                  }}
                  label="Upload Cover Image"
                  showRequired
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="news_letter"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomUpload
                  accepts=".pdf"
                  error={error}
                  fileName={value?.name}
                  handleBeforeUpload={(file) => {
                    const isValid = file?.name?.toLowerCase()?.endsWith(".pdf");
                    if (!isValid) {
                      toast.error("Only PDF files are allowed!");
                      return Upload.LIST_IGNORE;
                    }
                    onChange(file);
                    return false;
                  }}
                  label="Upload Newsletter PDF"
                  showOptional
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="title"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomTextfield
                  id="title"
                  label="Title"
                  showRequired
                  labelColor="#171717"
                  placeholder="Enter Title"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="communication_channel"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomSelect
                  id="communication_channel"
                  label="Channel of communication"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  size="large"
                  mode="multiple"
                  value={value}
                  onChange={(selected) => 
                    handleSelectAll(selected, COMMUNICATION_CHANNEL, onChange)
                  }
                  status={error ? "error" : ""}
                >
                  {COMMUNICATION_CHANNEL?.map((item, index) => (
                    <Select.Option key={index} value={item?.value}>
                      {item?.label}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="audience"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomSelect
                  id="audience"
                  label="Select Audience"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  size="large"
                  mode="multiple"
                  value={value}
                  onChange={(selected) => 
                    handleSelectAll(selected, AUDIENCE_LIST, onChange)
                  }
                  status={error ? "error" : ""}
                >
                  {AUDIENCE_LIST?.map((item, index) => (
                    <Select.Option key={index} value={item?.value}>
                      {item?.label}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="levels"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomSelect
                  id="levels"
                  label="Select Level"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  size="large"
                  mode="multiple"
                  value={value}
                  onChange={(selected) => 
                    handleSelectAll(selected, allLevels, onChange)
                  }
                  status={error ? "error" : ""}
                  allowClear
                >
                  {allLevels?.map((item, index) => (
                    <Select.Option key={index} value={item?.value}>
                      {item?.label}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name="schedule_published"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CustomSelect
                  id="schedule_published"
                  label="Publishing Schedule"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                >
                  {PUBLISHING_SCHEDULE?.map((item, index) => (
                    <Select.Option key={index} value={item?.value}>
                      {item?.label}
                    </Select.Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>

          {schedulePublished === "later" ? (
            <>
              <Col span={12}>
                <Controller
                  name="schedule_date"
                  control={control}
                  rules={{ required: true }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomDatepicker
                      id="schedule_date"
                      label="Schedule Date"
                      labelColor="#171717"
                      placeholder="Select Date"
                      size="large"
                      allowClear={false}
                      value={value}
                      onChange={onChange}
                      style={{ height: "40px", width: "100%" }}
                      status={error ? "error" : ""}
                      prefix={
                        <img
                          src="/assets/images/calendarOutlined.svg"
                          style={{ marginRight: 4 }}
                          alt="calendar"
                        />
                      }
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Controller
                  name="schedule_time"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <CustomTimePicker
                      {...field}
                      label="Schedule Time"
                      placeholder="Select Time"
                      size="large"
                      format="hh:mm a"
                      style={{ width: "100%", height: 40 }}
                      status={error ? "error" : ""}
                      prefix={
                        <ClockCircleOutlined
                          style={{ color: "#999999", fontSize: 20 }}
                        />
                      }
                    />
                  )}
                />
              </Col>
            </>
          ) : null}
          
          <Col span={24}>
            <Controller
              name="content"
              control={control}
              render={({ field: { onChange, value } }) => (
                <RichTextEditor value={value} onChange={onChange} />
              )}
            />
          </Col>
        </Row>

        <div className={styles.footerButtons}>
          <CustomButton
            onClick={() => navigate(-1)}
            title="Cancel"
            variant="outlined"
            className={styles.cancelButton}
          />
          <CustomButton
            onClick={handleSubmitForm}
            title={newsletterId ? "Update Newsletter" : "Create Newsletter"}
            type="submit"
            variant="outlined"
          />
        </div>
      </div>
    </Spin>
  );
};

export default NewsLetterForm;